import { UseQueryResult } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';

import { AddEditCrew } from '@/modules/workforce/Crew/components/Modals/AddEditCrew';
import { AddEditWorker } from '@/modules/workforce/Crew/components/Modals/AddEditWorker';
import { DeactivateCrewConfirmation } from '@/modules/workforce/Crew/components/Modals/DeactivateCrewConfirmation';
import { ISuppliers } from '@/modules/workforce/Crew/types';
import { Button, Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import DotedMenu from '@/shared/components/molecules/DotedMenu';
import QrCodeModal from '@/shared/components/molecules/QrCodeModal.tsx';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Archive from '~/images/shared/Archive.svg';
import AvatarSvg from '~/images/shared/avatar.svg';
import Edit from '~/images/shared/edit.svg';
import DotsMenu from '~/images/shared/HorizontalHollowDots.svg';
import Plus from '~/images/shared/Plus.svg';
import QRCode from '~/images/shared/scan-barcode-filled.svg';
import Trash from '~/images/shared/Trash.svg';

const Header: React.FC<{
  supplierData: UseQueryResult<ISuppliers.ISupplierDetails, unknown>;
}> = ({ supplierData }) => {
  const { user } = useAuth();
  const [isDemoFarm] = useStore((store) => store.isDemoFarm);
  const { t } = useTranslation('common');
  const [showQRCode, setShowQRCode] = useState(false);
  const { push, query, replace } = useRouter();
  const { openModal, closeModal } = useModal();
  const { data, isSuccess } = supplierData;
  const closeQrModalHandler = () => {
    setShowQRCode(false);
  };

  useEffect(() => {
    if (isSuccess) {
      replace({
        query: {
          ...query,
          idIdendifier_2: data.supplierName,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <div className='flex w-full flex-col  justify-between gap-16 tablet:flex-row'>
      <div className='flex w-full items-center justify-between'>
        <div className='flex gap-2'>
          <AvatarSvg width='48' height='48' />
          <div>
            <div className='flex items-center gap-2'>
              <Typography variant='h2' weight='medium'>
                {data?.supplierName}
              </Typography>
              {data?.status === 'INACTIVE' && (
                <span className='h-fit rounded-lg bg-error-90 px-16 py-1 text-14 font-semi-bold text-error-20'>
                  {t('inactive')}
                </span>
              )}
            </div>
            <div className='flex gap-5 text-grey-600'>
              <Typography>{t('registered-by')}:</Typography>
              <Typography>{data?.createdByName}</Typography>-
              <Typography>
                {data?.createdAt
                  ? format(new Date(data.createdAt), 'dd/MM/yyyy')
                  : 'Date not available'}
              </Typography>
            </div>
          </div>
        </div>
        <div className='flex items-center gap-16'>
          {data?.status === 'ACTIVE' && (
            <>
              <Button
                onClick={() =>
                  openModal(
                    <AddEditWorker
                      supplierId={Number(query.id)}
                      supplierName={data?.supplierName ?? ''}
                      initialValues={undefined}
                    />,
                    {
                      variant: 'full',
                      title: t('add-worker'),
                      titleVariant: 'h6',
                      onClose: () =>
                        openModal(
                          <ConfirmationModal
                            header={t('going-somewhere-else')}
                            message={t('you-will-lose-all-data')}
                            variant='warning'
                            dismissText={t('continue-editing')}
                            confirmText={t('discard-and-exit')}
                            onDismissRemoveNumber={1}
                            onConfirm={closeModal}
                            buttonsAlignment='col'
                            preferredAction='dismiss'
                          />
                        ),
                    }
                  )
                }
                variant='filled'
                size='none'
                className='!rounded-xl'
              >
                <span className='flex items-center gap-2 p-16 text-base font-medium'>
                  <BaseIcon size='sm'>
                    <Plus />
                  </BaseIcon>
                  {t('add-worker')}
                </span>
              </Button>
              <Button
                onClick={() =>
                  openModal(
                    <AddEditCrew
                      crewId={Number(query.id)}
                      initialValues={{
                        name: data?.supplierName ?? '',
                        phoneNumber: data?.phoneNumber,
                        countryCode: data?.country,
                        nationalIdBack: data?.nationalIdBack,
                        nationalIdFront: data?.nationalIdFront,
                      }}
                    />,
                    {
                      variant: 'full',
                      title: t('edit-crew'),
                      titleVariant: 'h6',
                      onClose: () =>
                        openModal(
                          <ConfirmationModal
                            header={t('going-somewhere-else')}
                            message={t('you-will-lose-all-data')}
                            variant='warning'
                            dismissText={t('continue-editing')}
                            confirmText={t('discard-and-exit')}
                            onDismissRemoveNumber={1}
                            onConfirm={closeModal}
                            buttonsAlignment='col'
                            preferredAction='dismiss'
                          />
                        ),
                    }
                  )
                }
                variant='text'
                size='none'
                className='!rounded-xl border-xs border-outline-default'
              >
                <span className='flex items-center gap-2 p-16 text-base font-medium'>
                  <BaseIcon size='sm'>
                    <Edit />
                  </BaseIcon>
                  {t('edit')}
                </span>
              </Button>
              <DotedMenu
                list={[
                  {
                    title: (
                      <div className='text-1 flex items-center gap-1 text-primary-default'>
                        <QRCode width={22} height={22} />
                        <span className='whitespace-nowrap'>
                          {t('show-qr-code')}
                        </span>
                      </div>
                    ),
                    id: '0',
                    action: () => setShowQRCode(true),
                  },
                  {
                    id: '2',
                    title: (
                      <div className='text-1 flex items-center gap-1 text-primary-default'>
                        <Archive />
                        <span className='whitespace-nowrap'>
                          {t('archived-workers')}
                        </span>
                      </div>
                    ),
                    action: () => {
                      push({
                        pathname: routes.inactiveWorkers,
                        query: {
                          id: query.id,
                        },
                      });
                    },
                  },

                  {
                    title: (
                      <div className='flex items-center gap-2 text-error-400'>
                        <Trash width='24' height='24' />
                        <span>{t('deactivate')}</span>
                      </div>
                    ),
                    id: '1',
                    action: () =>
                      isDemoFarm
                        ? openModal(<DemoCTA />)
                        : openModal(
                            <DeactivateCrewConfirmation
                              crewId={Number(query.id)}
                            />,
                            {
                              containerClassName: 'max-w-md',
                            }
                          ),
                  },
                ]}
                getTitle={(item) => item.title}
                getId={(item) => item?.id}
                getAction={(item) => item.action}
                onOpen={() => null}
                DotsMenuIcon={
                  <DotsMenu className='text-24 text-primary-default' />
                }
              />
            </>
          )}
        </div>
      </div>
      <QrCodeModal
        qrData={{
          qrCode: data?.qrCode,
          name: data?.supplierName,
          title: t('team_leader'),
          logo: user?.companyLogo,
        }}
        open={showQRCode}
        closeModal={closeQrModalHandler}
      />
    </div>
  );
};
export default Header;
